import { type ReactElement, type ReactNode } from "react";
import { Modal } from "@/components/ui/Modal";
import { CopyButton } from "./CopyButton";

interface Props {
  open: boolean;
  token: string | null;
  onClose: () => void;
  /** Headline copy varies between create ("Strategy created") and rotate ("Token rotated"). */
  title: string;
  description: string;
  /** Webhook strategies show URLs + token + snippet; an internal-engine
   * strategy (supertrend, ut_bot_stc) shows an info note instead (no
   * webhook exists for it). Defaults to true so existing callers keep
   * their behaviour. */
  webhook?: boolean;
  /** Which internal engine the note names — only read when `webhook` is
   * false. Defaults to "Supertrend" for backward compatibility with
   * existing callers. */
  engineLabel?: string;
  /** The engine's market-data venue named in the note — "Fyers" for
   * supertrend/ut_bot, "Zerodha" for bb_bottom_buy. Defaults to "Fyers"
   * for backward compatibility. Only read when `webhook` is false. */
  engineSource?: string;
  /** Optional webhook URLs to show alongside the token (create flow only). */
  entryUrl?: string;
  exitUrl?: string;
}

export function TokenRevealDialog({
  open,
  token,
  onClose,
  title,
  description,
  webhook = true,
  engineLabel = "Supertrend",
  engineSource = "Fyers",
  entryUrl,
  exitUrl,
}: Readonly<Props>): ReactElement {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      icon="ti-circle-check"
      iconTone="success"
      size="lg"
      footer={<DoneButton webhook={webhook} onClose={onClose} />}
    >
      <RevealBody
        token={token}
        webhook={webhook}
        engineLabel={engineLabel}
        engineSource={engineSource}
        {...(entryUrl !== undefined ? { entryUrl } : {})}
        {...(exitUrl !== undefined ? { exitUrl } : {})}
      />
      {webhook && <WarningFooter />}
    </Modal>
  );
}

function DoneButton({
  webhook,
  onClose,
}: Readonly<{ webhook: boolean; onClose: () => void }>): ReactElement {
  return (
    <button type="button" className="btn btn-primary" onClick={onClose}>
      <i className="ti ti-check text-sm" />
      {webhook ? "I've saved the token" : "Got it"}
    </button>
  );
}

interface RevealBodyProps {
  token: string | null;
  webhook: boolean;
  engineLabel: string;
  engineSource: string;
  entryUrl?: string;
  exitUrl?: string;
}

function RevealBody({
  token,
  webhook,
  engineLabel,
  engineSource,
  entryUrl,
  exitUrl,
}: Readonly<RevealBodyProps>): ReactElement {
  if (!webhook) return <EngineNote label={engineLabel} source={engineSource} />;
  return (
    <>
      {entryUrl !== undefined && (
        <Field label="Webhook URL (entry)">
          <ReadOnlyRow value={entryUrl} mono />
        </Field>
      )}
      {exitUrl !== undefined && (
        <Field label="Webhook URL (exit)">
          <ReadOnlyRow value={exitUrl} mono />
        </Field>
      )}
      {token !== null && <TokenField token={token} />}
      {token !== null && <SnippetField token={token} />}
    </>
  );
}

function EngineNote({ label, source }: Readonly<{ label: string; source: string }>): ReactElement {
  return (
    <div className="flex items-start gap-2 rounded-ctl bg-surface-2 p-3 text-sm text-ink-secondary">
      <i className="ti ti-chart-line text-base text-accent" />
      <span>
        This is a <span className="font-medium text-ink">{label}</span> strategy — the engine
        generates entry/exit signals from {source} data on its own. There&apos;s no webhook URL or
        TradingView alert to set up. Open the strategy to set exit mode, indicator settings, lots
        and SL/target, and make sure {source} is connected on the Broker page.
      </span>
    </div>
  );
}

function TokenField({ token }: Readonly<{ token: string }>): ReactElement {
  return (
    <Field
      label={
        <span className="inline-flex items-center gap-2">
          Access token
          <span className="pill bg-warning-bg text-warning">Shown once</span>
        </span>
      }
    >
      <ReadOnlyRow value={token} mono highlight />
    </Field>
  );
}

function SnippetField({ token }: Readonly<{ token: string }>): ReactElement {
  return (
    <Field label="Paste into your TradingView alert message:">
      <div className="overflow-x-auto rounded-ctl bg-surface-2 px-3 py-2 font-mono text-xs">
        {`{"access_token":"${token}","alert_name":"Entry"}`}
      </div>
    </Field>
  );
}

function WarningFooter(): ReactElement {
  return (
    <div className="mt-3 flex items-start gap-2 rounded-ctl bg-warning-bg p-3 text-xs text-warning">
      <i className="ti ti-alert-triangle text-base" />
      <span>
        If you close this dialog without saving the token, you&apos;ll need to use Rotate token from
        the strategy page to get a new one.
      </span>
    </div>
  );
}

function Field({
  label,
  children,
}: Readonly<{ label: ReactNode; children: ReactNode }>): ReactElement {
  return (
    <div className="mb-3">
      <div className="label">{label}</div>
      {children}
    </div>
  );
}

interface ReadOnlyRowProps {
  value: string;
  mono?: boolean;
  highlight?: boolean;
}

function ReadOnlyRow({ value, mono, highlight }: Readonly<ReadOnlyRowProps>): ReactElement {
  const baseCls = "flex-1 overflow-x-auto rounded-ctl px-3 py-2 text-xs";
  const monoCls = mono === true ? " font-mono" : "";
  const borderCls =
    highlight === true
      ? " border-2 border-warning bg-surface-2"
      : " border border-line bg-surface-2";
  return (
    <div className="flex items-center gap-2">
      <div className={baseCls + monoCls + borderCls}>{value}</div>
      <CopyButton value={value} />
    </div>
  );
}
